<template>
  <v-card>
    <base-stripe-data-table
      ref="fetchData"
      :headers="headers"
      fetchAction="listPaymentLinks"
      itemsGetter="paymentLinks"
      storeNamespace="paymentLinks"
      :actionLoading="usePaymentLinksStore().loading"
    >
      <template v-slot:item="{ item }">
        <tr
          :key="asRow(item).id"
          style="cursor: pointer"
          @click="goPaymentLinkDetails(asRow(item).id)"
        >
          <td>
            <div>
              <v-tooltip right>
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-bind="props"
                    color="grey"
                    class="ml-2"
                    icon=" mdi-content-copy"
                    @click.stop="copyPaymentLinkUrl(item)"
                  />
                </template>
                <span>{{ $t("page.paymentLinks.copyLink") }}</span>
              </v-tooltip>
            </div>
          </td>
          <td>
            <div class="">{{ asRow(item).name }}</div>
            <div class="text-grey text-caption" v-truncate="[asRow(item).url ?? '', 40]" />
          </td>

          <td class="text-center text-grey">
            {{ getTimeFormattedWithAMAndPM(asRow(item).metadata?.created ?? 0) || "-" }}
          </td>

          <td class="text-center">
            <status-chip
              :status="asRow(item).active ? PaymentLinkStatus.ACTIVE : PaymentLinkStatus.INACTIVE"
            />
          </td>
          <td class="text-center">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" icon flat>
                  <v-icon color="grey">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, index) in asRow(item).active
                    ? activeActionList
                    : inactiveActionList"
                  :key="index"
                  :value="index"
                  :class="{
                    'dotted-line-below':
                      activeActionList[index + 1] &&
                      activeActionList[index + 1].title === $t('common.action.deactivate'),
                    'text-red': action.title === $t('common.action.deactivate'),
                    'text-green': action.title === $t('common.action.activate'),
                  }"
                  @click.stop="action.action(asRow(item).id, asRow(item).active ?? false)"
                >
                  <v-list-item-title
                    :class="{
                      'text-red': action.title === $t('common.action.deactivate'),
                    }"
                  >
                    {{ action.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </base-stripe-data-table>
  </v-card>
  <send-to-customer-dialog ref="sendDialog" />
</template>

<script lang="ts" setup>
import { watch, ref } from "vue";
import BaseStripeDataTable from "@/shared/components/Common/BaseStripeDataTable.vue";
import { usePaymentLinksStore } from "@/payment-links/stores/paymentLinks";
import { useRouter } from "vue-router";
import { getTimeFormattedWithAMAndPM } from "@/shared/utils/date.utils";
import { copyToClipboard } from "@/shared/utils/copyToClipboard.utils";
import SendToCustomerDialog from "@/payment-links/components/payment-link/SendToCustomerDialog.vue";
import StatusChip from "@/shared/components/Common/StatusChip.vue";
import { PaymentLinkStatus } from "@/shared/enums/status.enum";
import i18n from "@/app/plugins/i18n";
import type { PaymentLinkTableRow } from "@/payment-links/types/paymentLinks";
import type { DataTableHeader } from "vuetify";

const asRow = (item: { id?: string }): PaymentLinkTableRow => item as PaymentLinkTableRow;

const copyPaymentLinkUrl = (item: { id?: string }) => {
  copyToClipboard(asRow(item).url ?? "");
};

const sendDialog = ref<null | { open: (url?: string, name?: string) => null }>(null);
const router = useRouter();
const paymentLinksStore = usePaymentLinksStore();
const fetchData = ref<{ fetchData: () => Promise<void> } | null>(null);

watch(
  () => paymentLinksStore.isActive,
  async () => {
    await fetchData.value?.fetchData();
  },
);

const headers: DataTableHeader[] = [
  {
    title: "",
    align: "center",
    key: "copy",
  },
  {
    title: i18n.global.t("common.action.paymentLink"),
    align: "start",
    key: "url",
  },
  {
    title: i18n.global.t("common.action.created"),
    align: "center",
    key: "created",
  },
  {
    title: i18n.global.t("common.action.status"),
    align: "center",
    key: "active",
  },
  {
    title: "",
    align: "end",
    key: "menu",
    sortable: false,
  },
];

const actionList = [
  {
    title: i18n.global.t("common.action.edit"),
    action: (id: string) => {
      goUpdatePaymentLinks(id);
    },
  },
  {
    title: i18n.global.t("common.action.viewDetails"),
    action: (id: string) => {
      goPaymentLinkDetails(id);
    },
  },
];

const activeActionList = [
  ...actionList,
  {
    title: i18n.global.t("common.action.deactivate"),
    action: (id: string, active: boolean) => {
      activatePaymentLink(id, active);
    },
  },
];

const inactiveActionList = [
  ...actionList,
  {
    title: i18n.global.t("common.action.activate"),
    action: (id: string, active: boolean) => {
      activatePaymentLink(id, active);
    },
  },
];

const goPaymentLinkDetails = (id: string) => {
  router.push({ path: `/details/paymentLinks/${id}` });
};

const goUpdatePaymentLinks = (id: string) => {
  router.push({ path: `/paymentLinks/${id}` });
};

const activatePaymentLink = async (id: string, active: boolean) => {
  await usePaymentLinksStore().activatePaymentLink(id, !active, true);
};

const sendToCustomer = async (url: string, name?: string) => {
  sendDialog.value?.open(url, name);
};
</script>
<style>
.dotted-line-below {
  border-bottom: 1px dotted #ccc;
  padding-bottom: 8px;
  margin-bottom: 8px;
}
.text-red {
  color: red;
}

.delete-action {
  border-top: none !important;
}
</style>
